import { useMemo, useState } from 'react';
import { Descriptions, Flex, Modal, Select, Typography } from 'antd';

import CondensedTable from 'components/CondensedTable/CondensedTable';
import SectionMessage from 'components/SectionMessage/SectionMessage';
import { formatDate } from 'utilities/datesAndTimes';
import { SIZE, UPCOMING_CIT_STATUS } from 'utilities/constants';
import { STATUS } from 'utilities/constants';

const CIT_COLUMNS = Object.freeze([
  {
    title: 'Event',
    dataIndex: 'name',
  },
  {
    title: 'Date',
    dataIndex: 'timestamp',
    render: formatDate,
  },
]);

const citIsUpcoming = (cit) => {
  return UPCOMING_CIT_STATUS.includes(cit?.status);
};

const ChangeInTermsModal = ({ changeInTerms, ...modalProps }) => {
  const [selectedId, setSelectedId] = useState(undefined);

  const citByTargetDate = useMemo(() => {
    const citList = changeInTerms || [];
    const { upcoming, past } = citList.reduce(
      (results, cit) => {
        if (citIsUpcoming(cit)) {
          results.upcoming.push(cit);
        } else {
          results.past.push(cit);
        }
        return results;
      },
      { upcoming: [], past: [] },
    );

    const sortedCIT = [
      ...upcoming.sort((cit1, cit2) => {
        return new Date(cit1.targetDate) - new Date(cit2.targetDate);
      }),
      ...past.sort((cit1, cit2) => {
        return new Date(cit1.targetDate) - new Date(cit2.targetDate);
      }),
    ];

    if (sortedCIT.length) {
      setSelectedId(sortedCIT[0].id);
    }

    return sortedCIT;
  }, [changeInTerms]);

  const citOptions = citByTargetDate.map((cit) => {
    const isUpcoming = citIsUpcoming(cit);
    return {
      label: `${cit.noticeName} ${isUpcoming ? '(Upcoming)' : ''}`,
      value: cit.id,
    };
  });

  const selectedCIT = citByTargetDate.find((cit) => {
    return selectedId === cit.id;
  });

  const selectedCITEvents = (selectedCIT?.events || []).map((event, index) => {
    return {
      key: `${event.name}-${index}`,
      name: event.name,
      timestamp: event.timestamp,
    };
  });

  return (
    <Modal
      {...modalProps}
      centered
      width={640}
      cancelButtonProps={{ hidden: true }}
      title="Change In Terms"
    >
      {!citByTargetDate.length ? (
        <SectionMessage
          data-testid="no-cit-history-message"
          text={'This customer has no "Change in Terms" history.'}
          status={STATUS.INFORMATION}
          size={SIZE.SM}
        />
      ) : (
        <>
          <Select
            data-testid="cit-select"
            value={selectedId}
            options={citOptions}
            onChange={(value) => {
              setSelectedId(value);
            }}
            style={{
              width: '100%',
              maxWidth: '300px',
              marginBottom: 'var(--spacing)',
            }}
          />

          <Flex vertical={true} gap="var(--spacing-lg)">
            <div>
              <Typography.Paragraph strong>CIT Details</Typography.Paragraph>
              <Descriptions
                key={selectedCIT?.id}
                bordered={true}
                size="small"
                column={1}
              >
                <Descriptions.Item label="Name">
                  {selectedCIT?.noticeName}
                </Descriptions.Item>
                <Descriptions.Item label="Status">
                  {selectedCIT?.status}
                </Descriptions.Item>
                <Descriptions.Item label="Target Date">
                  {formatDate(selectedCIT?.targetDate)}
                </Descriptions.Item>
                <Descriptions.Item label="Upcoming CAG">
                  <a
                    data-testid="cit-cag-url"
                    target="_blank"
                    rel="noreferrer"
                    href={selectedCIT?.cagUrl}
                  >
                    Click to Open
                  </a>
                </Descriptions.Item>
              </Descriptions>
            </div>
            <div>
              <Typography.Paragraph strong>Events</Typography.Paragraph>
              <div>
                <CondensedTable
                  data={selectedCITEvents}
                  columns={CIT_COLUMNS}
                  bordered={true}
                  size="small"
                  locale={{
                    emptyText: selectedCIT
                      ? 'Selected CIT has no event history'
                      : 'No past CIT selected',
                  }}
                />
              </div>
            </div>
          </Flex>
        </>
      )}
    </Modal>
  );
};

export default ChangeInTermsModal;
